// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const SOCIAL_PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: '\uD83D\uDCD8', charLimit: 63206, followers: '2.4K', engagement: '3.2%', connected: true },
  { id: 'instagram', name: 'Instagram', icon: '\uD83D\uDCF7', charLimit: 2200, followers: '5.1K', engagement: '4.8%', connected: true },
  { id: 'linkedin', name: 'LinkedIn', icon: '\uD83D\uDCBC', charLimit: 3000, followers: '1.8K', engagement: '2.1%', connected: true },
  { id: 'twitter', name: 'X / Twitter', icon: '\uD83D\uDCAC', charLimit: 280, followers: '890', engagement: '1.5%', connected: false },
  { id: 'tiktok', name: 'TikTok', icon: '\uD83C\uDFB5', charLimit: 2200, followers: '320', engagement: '6.2%', connected: false },
  { id: 'pinterest', name: 'Pinterest', icon: '\uD83D\uDCCC', charLimit: 500, followers: '450', engagement: '2.8%', connected: false },
  { id: 'youtube', name: 'YouTube', icon: '\u25B6\uFE0F', charLimit: 5000, followers: '210', engagement: '3.5%', connected: false },
];

const DEMO_POSTS = [
  { id: 1, text: 'Excited to announce our new spring collection! Quality craftsmanship meets modern design.', platforms: ['facebook', 'instagram'], date: 'Feb 20, 2026', time: '10:00 AM', status: 'Scheduled', likes: 0, comments: 0 },
  { id: 2, text: 'Behind the scenes of our workshop today. This is where the magic happens!', platforms: ['instagram', 'tiktok'], date: 'Feb 19, 2026', time: '2:30 PM', status: 'Published', likes: 142, comments: 23 },
  { id: 3, text: 'We\'re hiring! Join our growing team. Link in bio for open positions.', platforms: ['linkedin', 'facebook', 'twitter'], date: 'Feb 18, 2026', time: '9:00 AM', status: 'Published', likes: 87, comments: 12 },
  { id: 4, text: '5 tips to improve your business workflow this week. Thread below.', platforms: ['twitter', 'linkedin'], date: 'Feb 17, 2026', time: '11:00 AM', status: 'Published', likes: 54, comments: 8 },
];

const CONTENT_IDEAS = [
  { id: 1, type: 'Educational', title: 'How-to guide: Getting started with our platform', hashtags: '#tutorial #howto #business' },
  { id: 2, type: 'Behind-the-scenes', title: 'A day in the life at our office', hashtags: '#behindthescenes #teamwork #culture' },
  { id: 3, type: 'Testimonial', title: 'Customer spotlight: Success stories', hashtags: '#testimonial #success #customer' },
  { id: 4, type: 'Industry News', title: 'Top trends shaping our industry in 2026', hashtags: '#trends #industry #2026' },
  { id: 5, type: 'Promotional', title: 'Limited time offer for new customers', hashtags: '#sale #offer #deals' },
  { id: 6, type: 'Engagement', title: 'Poll: What feature would you love to see next?', hashtags: '#poll #feedback #community' },
  { id: 7, type: 'Holiday', title: 'Family Day weekend plans? Share yours!', hashtags: '#familyday #weekend #canada' },
];

const CALENDAR_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

function SocialMediaPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('calendar');
  const [postText, setPostText] = useState('');
  const [selectedPostPlatforms, setSelectedPostPlatforms] = useState(['facebook', 'instagram']);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  const togglePostPlatform = (id) => {
    setSelectedPostPlatforms(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const minCharLimit = selectedPostPlatforms.reduce((min, pid) => {
    const p = SOCIAL_PLATFORMS.find(sp => sp.id === pid);
    return p ? Math.min(min, p.charLimit) : min;
  }, 99999);

  const renderCalendar = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Content Calendar — February 2026</h2>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} style={{ padding: '10px 8px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {CALENDAR_DAYS.map(day => {
            const dayPosts = DEMO_POSTS.filter(p => parseInt(p.date.split(' ')[1]) === day);
            return (
              <div key={day} style={{ padding: '8px', minHeight: 80, borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', background: day === 20 ? 'rgba(99,102,241,0.06)' : 'transparent' }}>
                <span style={{ fontSize: 13, fontWeight: day === 20 ? 700 : 400, color: day === 20 ? '#6366f1' : 'var(--text)' }}>{day}</span>
                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {dayPosts.map(p => (
                    <div key={p.id} style={{ display: 'flex', gap: 2 }}>
                      {p.platforms.map(pid => {
                        const pl = SOCIAL_PLATFORMS.find(s => s.id === pid);
                        return <span key={pid} style={{ fontSize: 10 }} title={pl?.name}>{pl?.icon}</span>;
                      })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderComposer = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Create Post</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
        <div>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <label style={labelStyle}>Post Content</label>
            <textarea value={postText} onChange={e => setPostText(e.target.value)} placeholder="What do you want to share with your audience?" rows={6} style={{ ...inputStyle, resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <span style={{ fontSize: 12, color: postText.length > minCharLimit ? '#ef4444' : 'var(--text-muted)' }}>{postText.length}/{minCharLimit} characters</span>
              <button style={{ padding: '6px 14px', background: 'rgba(99,102,241,0.1)', color: '#6366f1', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>{'\u2728'} AI Write</button>
            </div>
          </div>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <label style={labelStyle}>Platforms</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {SOCIAL_PLATFORMS.map(p => {
                const sel = selectedPostPlatforms.includes(p.id);
                return (
                  <button key={p.id} onClick={() => togglePostPlatform(p.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: sel ? 'rgba(99,102,241,0.1)' : 'var(--bg)', border: sel ? '1px solid #6366f1' : '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', color: sel ? '#6366f1' : 'var(--text-muted)', fontSize: 13 }}>
                    <span>{p.icon}</span> {p.name} {sel && '\u2713'}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
            <label style={labelStyle}>Schedule</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} style={inputStyle} />
              <input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button style={{ flex: 1, padding: '10px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Schedule Post</button>
              <button style={{ padding: '10px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Post Now</button>
            </div>
          </div>
        </div>
        <div>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Hashtag Suggestions</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {['#business', '#growth', '#entrepreneur', '#smallbiz', '#innovation', '#tech', '#startup', '#marketing'].map(h => (
                <span key={h} onClick={() => setPostText(prev => prev + ' ' + h)} style={{ padding: '4px 10px', background: 'var(--bg)', borderRadius: 6, fontSize: 12, color: '#6366f1', cursor: 'pointer', border: '1px solid var(--border)' }}>{h}</span>
              ))}
            </div>
          </div>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Media</h3>
            <div style={{ border: '2px dashed var(--border)', borderRadius: 8, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{'\uD83D\uDDBC\uFE0F'}</div>
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Drop image or video here</p>
              <p style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>JPG, PNG, MP4 (max 50MB)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Social Analytics</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {SOCIAL_PLATFORMS.filter(p => p.connected).map(p => (
          <div key={p.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 24 }}>{p.icon}</span>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{p.name}</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><p style={{ fontSize: 22, fontWeight: 700, color: '#6366f1' }}>{p.followers}</p><p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Followers</p></div>
              <div><p style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>{p.engagement}</p><p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Engagement</p></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderIdeas = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Content Ideas</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>AI-generated content suggestions based on your business and trending topics.</p>
      <div style={{ display: 'grid', gap: 10 }}>
        {CONTENT_IDEAS.map(idea => (
          <div key={idea.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 12, padding: '4px 10px', background: 'rgba(99,102,241,0.1)', color: '#6366f1', borderRadius: 6, fontWeight: 500, whiteSpace: 'nowrap' }}>{idea.type}</span>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>{idea.title}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{idea.hashtags}</p>
            </div>
            <button onClick={() => { setPostText(idea.title); setTab('compose'); }} style={{ padding: '6px 14px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 6, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>Use This</button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderQueue = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Scheduled Queue</h2>
      <div style={{ display: 'grid', gap: 10 }}>
        {DEMO_POSTS.filter(p => p.status === 'Scheduled').map(p => (
          <div key={p.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ minWidth: 80, textAlign: 'center' }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#6366f1' }}>{p.date.split(',')[0].split(' ').slice(0, 2).join(' ')}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.time}</p>
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, color: 'var(--text)', marginBottom: 4 }}>{p.text.substring(0, 80)}...</p>
              <div style={{ display: 'flex', gap: 4 }}>
                {p.platforms.map(pid => {
                  const pl = SOCIAL_PLATFORMS.find(s => s.id === pid);
                  return <span key={pid} style={{ fontSize: 14 }} title={pl?.name}>{pl?.icon}</span>;
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={{ padding: '6px 12px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Edit</button>
              <button style={{ padding: '6px 12px', background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        ))}
        {DEMO_POSTS.filter(p => p.status === 'Scheduled').length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No scheduled posts. Create one from the Compose tab.</div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{'\uD83D\uDCF1'} Social Media</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 15, maxWidth: 600 }}>Manage organic social content across all platforms from one place.</p>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {['calendar', 'compose', 'analytics', 'ideas', 'queue'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 20px', background: tab === t ? '#6366f1' : 'var(--card)', color: tab === t ? '#fff' : 'var(--text-muted)', border: tab === t ? 'none' : '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>
      {tab === 'calendar' && renderCalendar()}
      {tab === 'compose' && renderComposer()}
      {tab === 'analytics' && renderAnalytics()}
      {tab === 'ideas' && renderIdeas()}
      {tab === 'queue' && renderQueue()}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default SocialMediaPage;
